export const CHECKOUT_URL = 'https://trtdesk-en.impultienda.ar/checkout/'

export const HERO_IMAGE =
  'https://cdn.magicpatterns.com/uploads/taf15SJmaPmJhBmaTcvuG6/TRTDESK-EN.png'

export const AUTHOR_IMAGE =
  'https://cdn.magicpatterns.com/uploads/vMQCr4n3g3R2eDc6C7VSaG/Dra_Karito_real.png'

export const withoutDeck: string[] = [
  'You open multiple guidelines to answer one question.',
  'You search through articles and clinical resources.',
  'You jump between testosterone, SHBG, LH, FSH, prolactin, and estradiol references.',
  'You try to remember what information you need to review.',
  'One laboratory result creates another question.',
  'A patient presents with a scenario that doesn’t fit the typical case.',
  'And your consultation becomes another research session.',
]

export const withDeck: string[] = [
  'You identify the scenario.',
  'You find the corresponding card.',
  'You review the relevant information.',
  'You follow the structured pathway.',
  'You identify what needs further review.',
  'And you move to the next part of the consultation with a clearer process.',
]

export const painPoints: { emoji: string; text: string }[] = [
  {
    emoji: '😰',
    text: 'You have a patient with a complex hormone panel and aren’t immediately sure where to begin.',
  },
  {
    emoji: '⏱️',
    text: 'You spend too much time searching through different resources before or during a consultation.',
  },
  {
    emoji: '🤔',
    text: 'You know the guidelines exist, but turning all that information into a practical sequence can be difficult.',
  },
  {
    emoji: '🧪',
    text: 'A panel containing testosterone, SHBG, LH, FSH, prolactin, and estradiol creates more questions than answers.',
  },
  {
    emoji: '🩺',
    text: 'A patient brings up fertility, monitoring, prostate-related concerns, or another clinical consideration and you need to organize your review.',
  },
  {
    emoji: '😓',
    text: 'Every TRT consultation feels like starting from scratch because you don’t have a visual navigation system.',
  },
]

export interface Volume {
  number: string
  title: string
  tagline: string
  description: string
  listLabel: string
  items: string[]
  note?: string
}

export const volumes: Volume[] = [
  {
    number: 'VOLUME I',
    title: 'THE TRT ENTRY DECK™',
    tagline: 'ORGANIZE THE INITIAL EVALUATION BEFORE MOVING FORWARD',
    description:
      'A structured collection of cards designed to help organize the initial review of a TRT consultation.',
    listLabel: 'YOU’LL FIND:',
    items: [
      'Initial Consultation Cards',
      'Clinical Assessment Cards',
      'Biochemical Review Cards',
      'Diagnostic Pathway Cards',
      'Differential Consideration Cards',
      'Reversible-Cause Review Cards',
      'TRT Evaluation Map',
      'Initial Consultation Checklist',
    ],
  },
  {
    number: 'VOLUME II',
    title: 'THE HORMONE INTERPRETATION DECK™',
    tagline: 'TURN A COMPLEX HORMONE PANEL INTO A VISUAL REVIEW PATH',
    description:
      'Organize the major hormone markers within their broader clinical context instead of looking at isolated numbers.',
    listLabel: 'CARDS COVER:',
    items: [
      'Total Testosterone',
      'Free Testosterone',
      'SHBG',
      'Albumin',
      'LH',
      'FSH',
      'Prolactin',
      'Estradiol',
      'Hormonal Patterns',
      'Clinical Context',
      'Laboratory Review',
      'Pattern Recognition',
    ],
  },
  {
    number: 'VOLUME III',
    title: 'THE TRT PATHWAY DECK™',
    tagline: 'FIND THE PATHWAY THAT MATCHES THE SCENARIO YOU’RE REVIEWING',
    description:
      'A collection of cards organized around major TRT decision points and treatment-related considerations.',
    listLabel: 'INCLUDES:',
    items: [
      'Treatment consideration cards',
      'Formulation comparison references',
      'Treatment-response review',
      'Adjustment considerations',
      'Follow-up pathways',
      'Reassessment cards',
      'Clinical context cards',
      'Decision-point maps',
    ],
    note: 'The goal isn’t to memorize more information. It’s to know what information needs to be reviewed for the scenario in front of you.',
  },
  {
    number: 'VOLUME IV',
    title: 'THE FERTILITY & HORMONAL BALANCE DECK™',
    tagline: 'NAVIGATE FERTILITY-RELATED QUESTIONS WITH A STRUCTURED FRAMEWORK',
    description:
      'A dedicated collection for one of the most challenging areas of hormone therapy.',
    listLabel: 'INCLUDES:',
    items: [
      'Fertility consideration cards',
      'Hormonal suppression cards',
      'hCG reference cards',
      'SERM reference cards',
      'Clomiphene reference cards',
      'Fertility-preservation considerations',
      'Scenario cards',
      'Clinical question prompts',
    ],
  },
  {
    number: 'VOLUME V',
    title: 'THE COMPLEX PATIENT DECK™',
    tagline: 'WHEN THE PATIENT DOESN’T FIT THE TEXTBOOK SCENARIO',
    description:
      'Cards designed to help structure the review of complex clinical scenarios and additional factors that may affect evaluation.',
    listLabel: 'INCLUDES:',
    items: [
      'Obesity scenarios',
      'Type 2 diabetes scenarios',
      'Metabolic syndrome',
      'Cardiovascular considerations',
      'Older patient scenarios',
      'Special population cards',
      'Risk assessment prompts',
      'Complex-case pathways',
      '“What should I review next?” cards',
    ],
  },
  {
    number: 'VOLUME VI',
    title: 'THE MONITORING & REASSESSMENT DECK™',
    tagline: 'BECAUSE THE CONSULTATION DOESN’T END WHEN TREATMENT BEGINS',
    description:
      'Organize longitudinal review around key monitoring and reassessment considerations.',
    listLabel: 'INCLUDES:',
    items: [
      'Treatment response cards',
      'Testosterone monitoring',
      'Hematocrit review',
      'PSA considerations',
      'Estradiol review',
      'Adverse-event review',
      'Dose reassessment',
      'Follow-up cards',
      'Long-term monitoring',
      'Treatment reassessment',
    ],
  },
]

export const alsoReceive: { emoji: string; title: string; text: string }[] = [
  {
    emoji: '🃏',
    title: '60+ CLINICAL DECISION CARDS',
    text: 'Each card is built around a specific question, scenario, or decision point.',
  },
  {
    emoji: '🧭',
    title: 'VISUAL NAVIGATION MAPS',
    text: 'Move from: QUESTION → SCENARIO → CARD → PATHWAY',
  },
  {
    emoji: '🔬',
    title: 'HORMONE INTERPRETATION CARDS',
    text: 'Quick visual references for organizing the review of key hormone markers in context.',
  },
  {
    emoji: '📋',
    title: 'CONSULTATION CHECKLISTS',
    text: 'Practical checklists for initial review and follow-up.',
  },
  {
    emoji: '🧩',
    title: 'COMPLEX SCENARIO CARDS',
    text: 'Structured references for situations that require additional clinical review.',
  },
  {
    emoji: '📚',
    title: 'MASTER INDEX',
    text: 'Find the card related to your question without searching through hundreds of pages.',
  },
]

export interface Bonus {
  label: string
  title: string
  value: string
  image: string
  description: string
  listLabel?: string
  items?: string[]
  footer?: string
}

export const bonuses: Bonus[] = [
  {
    label: 'BONUS #1',
    title: 'TRT 5-MINUTE CONSULTATION MAP™',
    value: 'VALUE $47',
    image:
      'https://cdn.magicpatterns.com/uploads/a2NahVVBiJLDop3Q5SrUNo/BONUS_1_TRTDESK-EN.png',
    description: 'A one-page visual map organizing the consultation workflow:',
    listLabel: 'ENTRY → ASSESSMENT → DATA → INTERPRETATION → SCENARIO → REVIEW → FOLLOW-UP',
    footer: 'Designed as a quick reference you can keep at your desk.',
  },
  {
    label: 'BONUS #2',
    title: 'TRT RAPID REFERENCE CARDS™',
    value: 'VALUE $39',
    image:
      'https://cdn.magicpatterns.com/uploads/47JGgkf2GTSKCBkPm1ei5g/BONUS_2_TRTDESK-EN.png',
    description:
      '20 additional quick-reference cards covering common consultation and follow-up scenarios.',
    listLabel: 'INCLUDES:',
    items: [
      'Quick Lab Cards',
      'Monitoring Cards',
      'Consultation Cards',
      'Follow-Up Cards',
      'Reassessment Cards',
    ],
  },
  {
    label: 'BONUS #3',
    title: 'TRT CASE ROOM™',
    value: 'VALUE $57',
    image:
      'https://cdn.magicpatterns.com/uploads/epkrMTqTWiGs1PM57fVsdi/BONUS_3_TRTDESK-EN.png',
    description:
      '30 structured clinical cases designed to help you practice navigating from presentation to relevant review points.',
    listLabel: 'EACH CASE INCLUDES:',
    items: [
      'Clinical presentation',
      'Relevant data',
      'Laboratory findings',
      'Review questions',
      'Clinical interpretation',
      'Decision pathway',
      'Follow-up considerations',
      'Clinical takeaways',
    ],
  },
  {
    label: 'BONUS #4',
    title: 'TRT CONSULTATION CHECKLIST PACK™',
    value: 'VALUE $27',
    image:
      'https://cdn.magicpatterns.com/uploads/3yR27ygTCcZMYinKfvuupU/BONUS_4_TRTDESK-EN.png',
    description: 'A professional checklist collection for organizing:',
    items: [
      'Initial consultation',
      'Laboratory review',
      'Follow-up',
      'Monitoring',
      'Treatment response',
      'Reassessment',
      'Complex scenarios',
      'Documentation',
    ],
  },
  {
    label: 'BONUS #5',
    title: 'TRT PROBLEM-SOLVING MINI DECK™',
    value: 'VALUE $27',
    image:
      'https://cdn.magicpatterns.com/uploads/sx9xaT1fhCD9iAuUg3iurN/BONUS_5_TRTDESK-EN.png',
    description:
      '20 additional cards built around situations that may require more structured review.',
    listLabel: 'INCLUDES SCENARIOS INVOLVING:',
    items: [
      'Elevated hematocrit',
      'PSA changes',
      'Estradiol concerns',
      'Fertility questions',
      'Lack of expected response',
      'Treatment reassessment',
      'Adverse events',
    ],
  },
]

export interface Testimonial {
  quote: string
  name: string
  role: string
  image: string
}

export const testimonials: Testimonial[] = [
  {
    quote:
      'Instead of searching through multiple resources, I can start with the card that matches the scenario I’m reviewing.',
    name: 'Dr. Andrés Villalba',
    role: 'Family Medicine · Bogotá, Colombia',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/cb2bd82e-938f-45ae-9692-abc5f9ab3cd1.jpg',
  },
  {
    quote:
      'The hormone interpretation cards help me organize the review of a laboratory panel without starting from scratch every time.',
    name: 'Dr. Mariana Cortés',
    role: 'Endocrinology · Monterrey, Mexico',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/1c91be66-a4e6-45ed-9c77-c93e13e87f6a.jpg',
  },
  {
    quote:
      'The complex-scenario cards are particularly useful when a patient doesn’t fit the straightforward case.',
    name: 'Dr. Rafael Ibarra',
    role: 'Urology · Santiago, Chile',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/a72f9e28-93ad-41fa-b4eb-219f6af9e4c3.jpg',
  },
  {
    quote:
      'The consultation map and checklists became a practical reference for my workflow.',
    name: 'Dr. Julián Restrepo',
    role: 'Internal Medicine · Lima, Peru',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/7674f3c7-c428-4eb7-a38d-dff5e939c9ef.jpg',
  },
]

export const faqs: { q: string; a: string }[] = [
  {
    q: 'How will I receive the TRT Decision Deck™?',
    a: 'Immediately after purchase, you’ll receive instructions for accessing the digital materials.',
  },
  {
    q: 'Is this a physical product?',
    a: 'No. The TRT Decision Deck™ is a digital product designed to be used on a computer, tablet, or smartphone. Selected resources are also formatted for printing.',
  },
  {
    q: 'Is this a course?',
    a: 'No. It is a collection of decision cards, visual maps, clinical cases, checklists, and reference resources.',
  },
  {
    q: 'Who is it designed for?',
    a: 'It is designed as an educational and reference resource for qualified healthcare professionals who work with TRT-related consultations.',
  },
  {
    q: 'Do I need to be a specialist?',
    a: 'The material is intended for healthcare professionals with the appropriate clinical background to interpret and apply the educational content.',
  },
  {
    q: 'Do the cards replace clinical guidelines?',
    a: 'No. The TRT Decision Deck™ is an educational and reference resource. It does not replace professional clinical judgment, current clinical practice guidelines, official prescribing information, applicable regulations, or specialist consultation when appropriate.',
  },
  {
    q: 'Can I use it on my phone or tablet?',
    a: 'Yes. The digital materials are designed for convenient reference on computers, tablets, and smartphones.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. The launch offer is a one-time payment of $27.',
  },
  {
    q: 'How long do I have access?',
    a: 'You’ll receive lifetime access to the purchased materials, subject to the applicable access terms.',
  },
  {
    q: 'What if I don’t find it useful?',
    a: 'You have a 7-day guarantee. You can request a refund within the applicable guarantee period according to the refund policy.',
  },
]

export const purchaseNotifications: { name: string; country: string }[] = [
  { name: 'Dr. Andrés M.', country: 'Bogotá, Colombia' },
  { name: 'Dra. Lucía R.', country: 'Madrid, Spain' },
  { name: 'Dr. Fernando G.', country: 'Buenos Aires, Argentina' },
  { name: 'Dra. Camila S.', country: 'Santiago, Chile' },
  { name: 'Dr. Miguel Á.', country: 'Mexico City, Mexico' },
  { name: 'Dra. Valentina P.', country: 'Lima, Peru' },
  { name: 'Dr. Joaquín T.', country: 'Montevideo, Uruguay' },
  { name: 'Dra. Isabel F.', country: 'Quito, Ecuador' },
  { name: 'Dr. Ricardo N.', country: 'San José, Costa Rica' },
  { name: 'Dra. Paula C.', country: 'Barcelona, Spain' },
  { name: 'Dr. Esteban V.', country: 'Panama City, Panama' },
  { name: 'Dra. Daniela O.', country: 'Asunción, Paraguay' },
]
